import { cn } from "@/lib/utils";

interface Column<T> {
  key: string;
  header: string;
  render: (item: T) => React.ReactNode;
}

interface Props<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (item: T) => void;
  renderCard?: (item: T) => React.ReactNode;
  /** desktop: "table" (default) o "card" (griglia). Su mobile sempre card se renderCard c'è. */
  view?: "table" | "card";
}

export function DataTable<T>({ columns, data, onRowClick, renderCard, view = "table" }: Props<T>) {
  if (!renderCard) {
    return (
      <div className="bg-card rounded-lg border border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead>
            <tr className="bg-surface text-left">
              {columns.map((col) => (
                <th key={col.key} className="px-4 py-3 text-muted-foreground font-medium">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, i) => (
              <tr
                key={i}
                className={cn(
                  "border-t border-border",
                  onRowClick && "cursor-pointer hover:bg-surface"
                )}
                onClick={() => onRowClick?.(item)}
                role={onRowClick ? "button" : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(item);
                        }
                      }
                    : undefined
                }
              >
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-3">
                    {col.render(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={view === "card" ? "" : "bg-card rounded-lg border border-border overflow-hidden"}>
      <div className={view === "card" ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" : "sm:hidden divide-y divide-border"}>
        {data.map((item, i) => (
          <div
            key={i}
            onClick={() => onRowClick?.(item)}
            role={onRowClick ? "button" : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onKeyDown={
              onRowClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRowClick(item);
                    }
                  }
                : undefined
            }
            className={
              (view === "card" ? "bg-card border border-border rounded-xl " : "") +
              (onRowClick ? "cursor-pointer hover:bg-surface" : "")
            }
          >
            {renderCard(item)}
          </div>
        ))}
      </div>
      <div className={view === "card" ? "hidden" : "hidden sm:block overflow-x-auto"}>
        <table className="w-full text-sm min-w-[500px]">
          <thead>
            <tr className="bg-surface text-left">
              {columns.map((col) => (
                <th key={col.key} className="px-4 py-3 text-muted-foreground font-medium">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, i) => (
              <tr
                key={i}
                className={cn(
                  "border-t border-border",
                  onRowClick && "cursor-pointer hover:bg-surface"
                )}
                onClick={() => onRowClick?.(item)}
                role={onRowClick ? "button" : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(item);
                        }
                      }
                    : undefined
                }
              >
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-3">
                    {col.render(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
